// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonSizeVariants } from "@insite/mobius/Button";
import { FormFieldIcon, FormFieldPresentationPropsCommon } from "@insite/mobius/FormField/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import get from "@insite/mobius/utilities/get";
import { color } from "@werner/XDL/Utilities";
import Color from "color";
import { css } from "styled-components";

export interface VariantValues {
    fontSize: number;
    height: number;
    icon: number;
    iconPadding: number;
    descriptionLineSpacing: number;
    borderRadius: number;
    datePickerWidth: number;
    leftLabelPadding: number;
    button: ButtonSizeVariants;
    labelHeight: number;
}

interface SizeVariantValues {
    default: VariantValues;
    small: VariantValues;
}

export const sizeVariantValues: SizeVariantValues = {
    default: {
        fontSize: 15,
        height: 70,
        icon: 16,
        iconPadding: 20,
        descriptionLineSpacing: 10,
        borderRadius: 3,
        datePickerWidth: 180,
        leftLabelPadding: 20,
        button: "medium",
        labelHeight: 32,
    },
    small: {
        fontSize: 15,
        height: 46,
        icon: 16,
        iconPadding: 7,
        descriptionLineSpacing: 5,
        borderRadius: 3,
        datePickerWidth: 118,
        leftLabelPadding: 5,
        button: "small",
        labelHeight: 27,
    },
};

export const borderTypes = {
    error: {
        color: "colors.danger.main",
        width: 2,
        borderPaddingRule: "padding: 34px 20px 16px 20px;",
        textAreaBorderPaddingRule: "padding: 34px 20px 16px 20px;",
        underlinePaddingRule: "padding: 14px 20px 16px 20px;",
        textAreaUnderlinePaddingRule: "padding: 22px 20px;",
    },
    focus: {
        color: "colors.primary.main",
        width: 2,
        borderPaddingRule: "padding: 34px 20px 16px 20px;",
        textAreaBorderPaddingRule: "padding: 34px 20px 16px 20px;",
        underlinePaddingRule: "padding: 14px 20px 16px 20px;",
        textAreaUnderlinePaddingRule: "padding: 22px 20px;",
    },
    inactive: {
        color: "colors.common.ui1",
        width: 1,
        borderPaddingRule: "padding: 34px 20px 16px 20px;",
        textAreaBorderPaddingRule: "padding: 34px 20px 16px 20px;",
        underlinePaddingRule: "padding: 14px 20px 16px 20px;",
        textAreaUnderlinePaddingRule: "padding: 22px 20px;",
    },
} as const;

type BorderKind = FormFieldPresentationPropsCommon["border"];

const borderByState = (
    {
        border,
        _sizeVariant,
        theme,
    }: {
        border: BorderKind;
        _sizeVariant: keyof typeof sizeVariantValues;
        theme: BaseTheme;
    },
    state: keyof typeof borderTypes,
    textarea = false,
) => {
    const {
        width,
        color,
        borderPaddingRule,
        underlinePaddingRule,
        textAreaBorderPaddingRule,
        textAreaUnderlinePaddingRule,
    } = borderTypes[state];
    let borderStyle = "solid";
    if (state === "focus") {
        borderStyle = get(theme, "focus.style");
    }
    const colorProp = get(theme, color);
    /* Border radius of 0 is included to override macOS select default 5px radius */
    if (border === "rectangle") {
        return `border: ${width}px ${borderStyle} ${colorProp};
        border-radius: 0;
        ${textarea ? textAreaBorderPaddingRule : borderPaddingRule}`;
    }
    if (border === "rounded") {
        return `border: ${width}px ${borderStyle} ${colorProp};
        border-radius: ${sizeVariantValues[_sizeVariant].borderRadius}px;
        ${textarea ? textAreaBorderPaddingRule : borderPaddingRule}`;
    }
    return `border-style: ${borderStyle};
    border-width: 0 0 ${width}px 0;
    border-color: ${colorProp};
    ${textarea ? textAreaUnderlinePaddingRule : underlinePaddingRule}
    border-radius: 0;`;
};

export const disabledByType = ({ border, theme }: { border: BorderKind; theme: BaseTheme }) => {
    if (border === "rectangle" || border === "rounded") {
        const disabledBkg = color(get(theme, "colors.common.ui1"), 0.5);
        return css`
            color: ${disabledBkg};
            border-color: ${disabledBkg};
        `;
    }
    return "border-bottom-style: dotted;";
};

export default borderByState;
